import React, { useEffect, useState } from 'react';
import api from '../../common/api';
import makeStyles from "@material-ui/styles/makeStyles";
import { ConfirmDialog, MaterialTable, PageHeader } from '../../components';
import useTheme from '@material-ui/styles/useTheme';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import MedicineStorageMoveDialog from './components/MedicineStorageMoveDialog';
import PPMobileCardView from '../../components/PPMobileCardView';
import Icon from '@material-ui/core/Icon';
import AddCircleIcon from '@material-ui/icons/AddCircle';
import moment from 'moment';

const useStyles = makeStyles(theme => ({
    root: props => ({
        // margin: props.isDesktop ? theme.spacing(4) : theme.spacing(1)
    }),
    topBar: {
        marginBottom: theme.spacing(2)
    },
}));

const MedicineStorageMovePage = ({ showError, showMessage, match }) => {
    const [moveData, setMoveData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [currentMove, setCurrentMove] = useState({});
    const [showDialog, setShowDialog] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [editMode, setEditMode] = useState(false);
    const [materialTypeList, setMaterialTypeList] = useState([]);
    const [materialList, setMaterialList] = useState(null);
    const [farms, setFarms] = useState([]);
    const [availableStorage, setAvailableStorage] = useState(0);

    const theme = useTheme();
    const isDesktop = useMediaQuery(theme.breakpoints.up('md'), { defaultMatches: true });
    const classes = useStyles({ isDesktop });

    const getMaterialTypes = () => {
        const response = api.get('material-type');
        response.then(res => {
            const types = [];
            if (res?.Medicine) {
                types.push({ key: 0, value: res.Medicine[0].materialTypeId, type: res.Medicine[0].type });
            }
            if (res?.Vaccination) {
                types.push({ key: 1, value: res.Vaccination[0].materialTypeId, type: res.Vaccination[0].type });
            }
            setMaterialTypeList(types);
            setMaterialList(res);
        }).catch(err => {
            if (err.message) showError(err.message);
        });
        return response;
    };

    const getAllFarms = () => {
        const response = api.get('farm');
        response.then(res => {
            setFarms([...res]);
        }).catch(err => {
            if (err.message) showError(err.message);
        });
        return response;
    };

    const getAvailableStorage = (materialMasterId, date, storageID) => {
        showError('');
        const response = api.post(`/available-medicine-global-storage/${materialMasterId}`, {
            date,
            id: storageID
        });
        response.then(res => {
            setAvailableStorage(res?.availableStorage || 0);
        }).catch(err => {
            if (err.message) console.log('Available storage fetch error:', err.message);
        });
    };

    const getAllMoveData = () => {
        setShowDialog(false);
        showError('');
        showMessage('');
        const response = api.post('/get-all-medicine-storage-moves', {});
        response.then(res => {
            setMoveData([...res]);
        }).catch(err => {
            if (err.message) {
                if (err.message === 'Bad Request' || err.message === 'No data found')
                    showError('No Medicine Storage Moves Found');
                else
                    showError(err.message);
            } else {
                showError('Unknown Error');
            }
        });
        return response;
    };

    useEffect(() => {
        setLoading(true);
        Promise.all([getMaterialTypes(), getAllFarms(), getAllMoveData()]).finally(() => setLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const onNewMove = (event) => {
        setEditMode(false);
        setErrorMessage('');
        event.preventDefault();
        setShowDialog(true);
    };

    const handleClose = () => {
        setErrorMessage(null);
        setCurrentMove({});
        setShowDialog(false);
    };

    const handleDeleteClose = () => setShowDeleteConfirm(false);

    const onSave = (data, setPopupLoading) => {
        showMessage('');
        const response = api.post('/move-to-inventory-medicine-storage', data);
        response.then(res => {
            setMoveData([...moveData, res]);
            setShowDialog(false);
        }).catch(err => {
            if (err.message) {
                if (err.message === 'no stock') {
                    setErrorMessage('Material with available stock 0 cannot be added');
                } else {
                    setErrorMessage(err.message);
                }
            } else {
                setErrorMessage('Unknown Error');
            }
        }).finally(() => {
            setLoading(false);
            setPopupLoading(false);
        });
    };

    const onEdit = (data, setPopupLoading) => {
        const response = api.post(`/move-to-inventory-medicine-storage/${data.id}`, data);
        response.then(res => {
            const index = moveData.findIndex(d => d.id === data.id);
            setMoveData([...moveData.slice(0, index), { ...res }, ...moveData.slice(index + 1)]);
            setShowDialog(false);
        }).catch(err => {
            if (err.message) {
                if (err.message === 'no stock') {
                    setErrorMessage('Material with available stock 0 cannot be updated');
                } else {
                    setErrorMessage(err.message);
                }
            } else {
                setErrorMessage('Unknown Error');
            }
        }).finally(() => {
            setPopupLoading(false);
            setLoading(false);
        });
    };

    const onDeleteConfirm = () => {
        if (currentMove && currentMove.id) {
            setShowDeleteConfirm(false);
            setLoading(true);
            showError('');
            const response = api.delete(`/move-to-inventory-medicine-storage/${currentMove.id}`);
            response.then(res => {
                if (res) {
                    const index = moveData.findIndex(d => d.id === currentMove.id);
                    const updated = [...moveData];
                    updated.splice(index, 1);
                    setMoveData(updated);
                }
            }).catch(err => {
                if (err.message) showError(err.message);
                else showError('Unknown Error');
            }).finally(() => {
                setLoading(false);
                setCurrentMove({});
            });
        }
    };

    const onEditClick = async (row) => {
        setErrorMessage('');
        const typeIdx = materialTypeList.findIndex(t => t.type === row.materialType);
        const typeName = materialTypeList[typeIdx]?.type;
        const medIdx = materialList?.[typeName]?.findIndex(m => m.materialMasterId === row.materialMasterId) ?? -1;
        const farmIdx = farms.findIndex(f => f.id === row.farmId);
        getAvailableStorage(row.materialMasterId, row.date, row.id);
        setCurrentMove({
            ...row,
            materialTypeIdx: typeIdx,
            medicineIdx: medIdx,
            farmIdx,
        });
        setEditMode(true);
        setShowDialog(true);
    };

    const onDeleteClick = (row) => {
        setCurrentMove(row);
        setShowDeleteConfirm(true);
    };

    const actions = isDesktop ? [
        {
            icon: () => <Icon color='primary'>edit</Icon>,
            tooltip: 'Edit Move',
            onClick: (event, rowData) => onEditClick(rowData)
        },
        {
            icon: () => <Icon color='error'>delete</Icon>,
            tooltip: 'Delete Move',
            onClick: (event, rowData) => onDeleteClick(rowData)
        }
    ] : [];

    return (
        <div className={classes.root}>
            <PageHeader
                title="Move from Medicine Storage"
                actions={[
                    { label: 'Add Move to Farm', icon: <AddCircleIcon />, onClick: onNewMove, variant: 'contained', color: 'primary' },
                ]}
            />
            <MaterialTable
                isLoading={loading}
                columns={[
                    { title: 'No', field: 'id', hidden: !isDesktop },
                    {
                        title: 'Date', field: 'date', hidden: !isDesktop,
                        render: rowData => <span>{moment(rowData.date).format("DD-MM-YYYY")}</span>
                    },
                    { title: 'Material Type', field: 'materialType', hidden: !isDesktop },
                    isDesktop
                        ? { title: 'Material Name', field: 'materialName', defaultSort: 'asc' }
                        : {
                            title: 'Material Name',
                            field: 'materialName',
                            defaultSort: 'asc',
                            render: rowData => (
                                <PPMobileCardView
                                    title={moment(rowData.date).format("DD-MM-YYYY")}
                                    id={rowData.id}
                                    rows={[
                                        { label: 'Material Name', value: rowData.materialName, fullWidth: true, hideLabel: true, bold: true, fontSize: 14 },
                                        { label: 'Material Type', value: rowData.materialType },
                                        { label: 'Farm', value: rowData.farmName },
                                        { label: 'Quantity', value: rowData.quantity },
                                        { label: 'Cost Per Unit', value: rowData.costperunit?.toFixed(2) },
                                        { label: 'Cost', value: rowData.cost?.toFixed(2) },
                                    ]}
                                    menuItems={[
                                        { label: 'Edit', onClick: () => onEditClick(rowData) },
                                        { label: 'Delete', onClick: () => onDeleteClick(rowData) },
                                    ]}
                                />
                            ),
                            // Other columns are hidden on mobile, and material-table skips hidden columns when searching
                            customFilterAndSearch: (term, rowData) => {
                                const values = [
                                    rowData.id,
                                    moment(rowData.date).format("DD-MM-YYYY"),
                                    rowData.materialType,
                                    rowData.materialName,
                                    rowData.farmName,
                                    rowData.quantity,
                                    rowData.costperunit,
                                    rowData.cost,
                                ];
                                return values.some(value => value !== undefined && value !== null &&
                                    String(value).toLowerCase().includes(term.toLowerCase()));
                            },
                        },
                    { title: 'Farm', field: 'farmName', hidden: !isDesktop },
                    { title: 'Quantity', field: 'quantity', hidden: !isDesktop },
                    {
                        title: 'Cost Per Unit', field: 'costperunit', hidden: !isDesktop,
                        render: rowData => rowData.costperunit?.toFixed(2)
                    },
                    {
                        title: 'Cost', field: 'cost', hidden: !isDesktop,
                        render: rowData => rowData.cost?.toFixed(2)
                    },
                ]}
                data={moveData}
                title='Move from Medicine Storage'
                options={{
                    actionsColumnIndex: -1,
                    search: true,
                    showTitle: true,
                    toolbar: true,
                    header: isDesktop,
                }}
                actions={actions}
            />
            <MedicineStorageMoveDialog
                errorMessage={errorMessage}
                showDialog={showDialog}
                handleClose={handleClose}
                onSave={onSave}
                onEdit={onEdit}
                editMode={editMode}
                data={currentMove}
                deleteErrorMessage={() => setErrorMessage('')}
                materialTypeList={materialTypeList}
                materialList={materialList}
                farms={farms}
                availableStorage={availableStorage}
                getAvailableStorage={getAvailableStorage}
            />
            <ConfirmDialog
                showDialog={showDeleteConfirm}
                handleClose={handleDeleteClose}
                onConfirm={onDeleteConfirm}
                title='Delete Medicine Storage Move?'
                subText='This action will delete the medicine storage move. Please confirm.'
            />
        </div>
    );
};

export default MedicineStorageMovePage;
